// src/pages/sales/survey.tsx
import React, { useState, useEffect } from "react";

// Type for survey responses with index signature to allow dynamic question keys
type SurveyResponses = { [key: string]: string | string[] };

const GoogleAdsSurvey = () => {
  // State for tracking responses
  const [responses, setResponses] = useState<SurveyResponses>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionPath, setQuestionPath] = useState([]);
  const [surveyComplete, setSurveyComplete] = useState(false);
  const [requiredQuestionsComplete, setRequiredQuestionsComplete] =
    useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Define the initial questions (required for all users)
  const initialQuestions = [
    {
      id: "experience",
      type: "radio",
      required: true,
      question: "How would you describe your Google Ads experience?",
      options: [
        { value: "beginner", label: "Beginner (0-1 years)" },
        { value: "intermediate", label: "Intermediate (1-3 years)" },
        { value: "advanced", label: "Advanced (3-5 years)" },
        { value: "expert", label: "Expert (5+ years)" },
      ],
    },
    {
      id: "adSpend",
      type: "radio",
      required: true,
      question:
        "What's the approximate monthly ad spend you manage across all accounts?",
      options: [
        { value: "under5k", label: "Under $5,000" },
        { value: "5kTo25k", label: "$5,000-$25,000" },
        { value: "25kTo100k", label: "$25,000-$100,000" },
        { value: "100kTo500k", label: "$100,000-$500,000" },
        { value: "over500k", label: "Over $500,000" },
      ],
    },
    {
      id: "campaignTypes",
      type: "checkbox",
      required: true,
      question: "Which campaign types do you work with regularly?",
      options: [
        { value: "search", label: "Search" },
        { value: "display", label: "Display" },
        { value: "pMax", label: "Performance Max" },
        { value: "video", label: "Video/YouTube" },
        { value: "demandGen", label: "Demand Gen" },
        { value: "shopping", label: "Shopping" },
      ],
    },
    {
      id: "painPoints",
      type: "checkbox",
      required: true,
      question:
        "Which aspects of Google Ads management consume most of your time? (Select up to 3)",
      options: [
        {
          value: "campaignStructure",
          label: "Creating/optimizing campaign structure",
        },
        {
          value: "keywordManagement",
          label: "Keyword research and management",
        },
        { value: "negativeKeywords", label: "Negative keyword management" },
        { value: "adCreation", label: "Ad creation and testing" },
        {
          value: "budgetBidding",
          label: "Budget allocation and bid management",
        },
        { value: "reporting", label: "Performance analysis and reporting" },
        { value: "clientComms", label: "Client communication and reporting" },
        { value: "landingPages", label: "Landing page optimization" },
      ],
      maxSelections: 3,
    },
    {
      id: "automationPreference",
      type: "radio",
      required: true,
      question: "Which approach to automation do you prefer?",
      options: [
        { value: "ruleBased", label: "Rule-based systems I fully control" },
        {
          value: "aiSuggested",
          label: "AI-suggested changes I approve before implementation",
        },
        { value: "blended", label: "Blended approach with both rules and AI" },
        {
          value: "fullyAutomated",
          label: "Fully automated with minimal oversight required",
        },
      ],
    },
    {
      id: "primaryFeature",
      type: "checkbox",
      required: true,
      question:
        "Which feature categories would provide the MOST value to your workflow? (Select up to 5). We might ask a follow up question for each feature to get more details.",
      options: [
        { value: "auditRules", label: "Audit & Rule System" },
        { value: "negativeKeywords", label: "Negative Keyword Management" },
        { value: "keywordOptimization", label: "Keyword Optimization" },
        { value: "searchTerms", label: "Search Term Analysis" },
        { value: "adCreation", label: "Ad Creation & Testing" },
        { value: "campaignStructure", label: "Campaign Structure Tools" },
        { value: "competitors", label: "Competitor Analysis" },
        { value: "landingPages", label: "Landing Page Optimization" },
        { value: "assets", label: "Asset/Extension Management" },
      ],
      maxSelections: 5,
    },
  ];

  // Branching paths based on primaryFeature selection
  const branchingPaths = {
    auditRules: [
      {
        id: "ruleSystemPriority",
        type: "checkbox",
        required: false,
        question:
          "Which capabilities of a centralized rule system would be most valuable? (Select up to 2)",
        options: [
          {
            value: "preBuiltTemplates",
            label: "Library of pre-built rule templates",
          },
          {
            value: "visualBuilder",
            label: "Visual rule builder (no coding required)",
          },
          {
            value: "auditLog",
            label: "Historical audit log of all rule actions",
          },
          {
            value: "bulkApplication",
            label: "Bulk application across multiple accounts/campaigns",
          },
          {
            value: "aiSuggested",
            label: "AI-suggested rules based on account performance",
          },
        ],
        maxSelections: 2,
      },
      {
        id: "ruleFrequency",
        type: "radio",
        required: false,
        question:
          "How often would you ideally run different types of automated rules?",
        options: [
          {
            value: "multipleDaily",
            label: "Multiple times daily for critical metrics",
          },
          { value: "daily", label: "Daily for most optimizations" },
          { value: "weekly", label: "Weekly for strategic adjustments" },
          { value: "monthly", label: "Monthly for major strategic shifts" },
          { value: "varied", label: "Varied by rule type and importance" },
        ],
      },
      {
        id: "ruleActions",
        type: "checkbox",
        required: false,
        question:
          "What actions should automated rules be able to perform? (Select all that apply)",
        options: [
          {
            value: "pauseEnable",
            label: "Pause/enable campaigns or ad groups",
          },
          {
            value: "adjustBudgets",
            label: "Adjust budgets based on performance",
          },
          {
            value: "modifyBids",
            label: "Modify bids at keyword or ad group level",
          },
          {
            value: "manageKeywords",
            label: "Add/remove keywords or negative keywords",
          },
          { value: "adjustAudiences", label: "Adjust audience targeting" },
          { value: "alerts", label: "Generate and send performance alerts" },
          { value: "reports", label: "Create and distribute reports" },
        ],
      },
    ],
    negativeKeywords: [
      {
        id: "negativeKeywordFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which negative keyword management features would deliver the most value? (Select up to 2)",
        options: [
          {
            value: "autoSuggest",
            label: "Auto-suggested negatives from search term data",
          },
          {
            value: "conflictDetection",
            label: "Conflict detection with active keywords",
          },
          {
            value: "bulkImportExport",
            label: "Bulk import/export with version control",
          },
          {
            value: "impactPrediction",
            label: "Impact prediction (forecasted effect of changes)",
          },
          {
            value: "semanticGrouping",
            label: "AI-powered semantic grouping of negatives",
          },
        ],
        maxSelections: 2,
      },
      {
        id: "negativeOrganization",
        type: "radio",
        required: false,
        question: "How would you prefer to organize negative keywords?",
        options: [
          {
            value: "accountLevel",
            label: "Account-level shared negative lists",
          },
          { value: "campaignLevel", label: "Campaign-specific negative lists" },
          { value: "adGroupLevel", label: "Ad group level negatives" },
          {
            value: "thematicLists",
            label: "Thematic lists (e.g., brand terms, irrelevant industries)",
          },
          { value: "allLevels", label: "All of the above" },
        ],
      },
      {
        id: "negativeSuggestions",
        type: "radio",
        required: false,
        question:
          "What criteria should drive AI suggestions for negative keywords?",
        options: [
          {
            value: "performanceMetrics",
            label: "Performance metrics (low CTR, high cost, no conversions)",
          },
          {
            value: "semanticRelevance",
            label: "Semantic relevance to your products/services",
          },
          { value: "competitorTerms", label: "Competitor brand terms" },
          {
            value: "industryTerms",
            label: "Industry-specific irrelevant terms",
          },
          { value: "allCriteria", label: "All of the above" },
        ],
      },
    ],
    keywordOptimization: [
      {
        id: "keywordFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which keyword management features would you find most valuable? (Select up to 2)",
        options: [
          {
            value: "liveDashboard",
            label: "Live performance dashboard with key metrics",
          },
          { value: "bidRules", label: "Automated bid adjustment rules" },
          {
            value: "dynamicGrouping",
            label: "Dynamic keyword grouping via AI",
          },
          {
            value: "codeHooks",
            label: "Code hooks for custom analysis (Python/JS)",
          },
          { value: "alerts", label: "High-spend, low-ROI keyword alerts" },
        ],
        maxSelections: 2,
      },
      {
        id: "keywordMetrics",
        type: "checkbox",
        required: false,
        question:
          "Which metrics are most important in your keyword optimization decisions? (Select up to 3)",
        options: [
          { value: "qualityScore", label: "Quality Score" },
          { value: "ctr", label: "Click-Through Rate (CTR)" },
          { value: "convRate", label: "Conversion Rate" },
          { value: "cpa", label: "Cost Per Acquisition (CPA)" },
          { value: "roas", label: "Return on Ad Spend (ROAS)" },
          { value: "impressionShare", label: "Impression Share" },
          { value: "searchImprShare", label: "Search Impression Share" },
          { value: "avgPosition", label: "Average Position" },
        ],
        maxSelections: 3,
      },
      {
        id: "keywordAutomation",
        type: "radio",
        required: false,
        question:
          "For keyword management, what level of automation would you prefer?",
        options: [
          {
            value: "fullyManual",
            label: "Fully manual with better data visualization",
          },
          {
            value: "alertsOnly",
            label: "Alerts only (you take action manually)",
          },
          {
            value: "semiAuto",
            label: "Semi-automated (suggest changes for your approval)",
          },
          {
            value: "fullyAuto",
            label: "Fully automated with performance guardrails you set",
          },
        ],
      },
    ],
    searchTerms: [
      {
        id: "searchTermFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which search term analysis features would provide the most value? (Select up to 2)",
        options: [
          {
            value: "heatMapping",
            label: "Heat mapping by spend/conversion metrics",
          },
          {
            value: "oneClickAdd",
            label: "One-click add to keyword or negative lists",
          },
          {
            value: "termClustering",
            label: "Term clustering and visualization",
          },
          {
            value: "scheduledExports",
            label: "Scheduled exports to CSV or Google Sheets",
          },
          {
            value: "trendDetection",
            label: "AI flagging of emerging trend terms",
          },
        ],
        maxSelections: 2,
      },
      {
        id: "searchTermVisualization",
        type: "radio",
        required: false,
        question: "How would you prefer to visualize search term data?",
        options: [
          { value: "tabular", label: "Tabular format with advanced filtering" },
          { value: "wordClouds", label: "Word clouds weighted by key metrics" },
          {
            value: "semanticClusters",
            label: "Semantic clusters showing related terms",
          },
          {
            value: "comparative",
            label: "Comparative views (this period vs. previous)",
          },
          { value: "trendCharts", label: "Performance trend charts over time" },
        ],
      },
      {
        id: "searchTermThresholds",
        type: "radio",
        required: false,
        question:
          "What metrics would trigger you to take action on a search term?",
        options: [
          {
            value: "impressions",
            label: "Impressions above a certain threshold",
          },
          { value: "clicks", label: "Clicks above a certain threshold" },
          { value: "cost", label: "Cost above a certain threshold" },
          {
            value: "conversions",
            label: "Conversions below a certain threshold",
          },
          { value: "cpa", label: "CPA above a certain threshold" },
          { value: "multiple", label: "Multiple metrics in combination" },
        ],
      },
    ],
    adCreation: [
      {
        id: "adFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which ad creation and testing features would be most valuable? (Select up to 2)",
        options: [
          {
            value: "abDashboard",
            label: "Ad copy A/B test dashboard with statistical significance",
          },
          {
            value: "autoPause",
            label: 'Automated "pause loser" rule based on performance',
          },
          {
            value: "llmGeneration",
            label: "LLM-generated headline/description variants",
          },
          { value: "adStrength", label: "Real-time ad strength scoring" },
          {
            value: "batchUpload",
            label: "Code API for batch upload of new ads",
          },
        ],
        maxSelections: 2,
      },
      {
        id: "adMetrics",
        type: "checkbox",
        required: false,
        question:
          "Which metrics are most important when evaluating ad performance? (Select up to 3)",
        options: [
          { value: "ctr", label: "Click-Through Rate" },
          { value: "convRate", label: "Conversion Rate" },
          { value: "cpa", label: "Cost Per Acquisition" },
          { value: "roas", label: "Return on Ad Spend" },
          { value: "adRelevance", label: "Ad Relevance Score" },
          { value: "landingExp", label: "Landing Page Experience" },
          { value: "qualityScore", label: "Overall Quality Score" },
        ],
        maxSelections: 3,
      },
      {
        id: "adCreationApproach",
        type: "radio",
        required: false,
        question: "How would you prefer to create new ad content?",
        options: [
          { value: "manualCreation", label: "Manual creation from scratch" },
          {
            value: "aiSuggestions",
            label: "AI-suggested variations to review and edit",
          },
          {
            value: "templateBased",
            label: "Template-based system with fill-in options",
          },
          {
            value: "competitorInspired",
            label: "Inspired by competitor ad analysis",
          },
          {
            value: "hybrid",
            label: "Hybrid approach combining multiple methods",
          },
        ],
      },
    ],
    // Additional branching paths for other primary features...
    campaignStructure: [
      {
        id: "campaignStructureFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which campaign structure tools would be most valuable? (Select up to 2)",
        options: [
          {
            value: "templateLibrary",
            label: "Template library for different campaign types",
          },
          {
            value: "skagCheck",
            label: "Automated SKAG/SKA-dead check & restructure",
          },
          {
            value: "llmRecommendations",
            label: "LLM-generated structure recommendations",
          },
          {
            value: "bulkClone",
            label: "Bulk clone/copy campaigns with preset rules",
          },
          {
            value: "treeMap",
            label: "High-level performance tree map visualization",
          },
        ],
        maxSelections: 2,
      },
      {
        id: "restructureThresholds",
        type: "radio",
        required: false,
        question: "What would trigger you to restructure a campaign?",
        options: [
          { value: "performance", label: "Declining performance metrics" },
          { value: "keywords", label: "Too many keywords in an ad group" },
          { value: "adgroups", label: "Too many ad groups in a campaign" },
          { value: "budget", label: "Budget allocation inefficiency" },
          { value: "strategy", label: "Strategic business changes" },
        ],
      },
    ],
    competitors: [
      {
        id: "competitorFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which competitor analysis features would be most valuable? (Select up to 2)",
        options: [
          {
            value: "adCopyScraping",
            label: "Auto-scrape competitor ad copy and landing URLs",
          },
          { value: "shareOfVoice", label: "Share-of-voice & overlap analysis" },
          {
            value: "llmSummary",
            label: "LLM-summarized competitor strengths/weaknesses",
          },
          { value: "bidAlerts", label: "Alerts on competitor bid spikes" },
          {
            value: "creativeBenchmarking",
            label: "Creative benchmarking charts",
          },
        ],
        maxSelections: 2,
      },
      {
        id: "competitorTracking",
        type: "checkbox",
        required: false,
        question:
          "Which competitor aspects would you like to track? (Select all that apply)",
        options: [
          { value: "keywords", label: "Keywords they bid on" },
          { value: "adCopy", label: "Ad copy and messaging" },
          { value: "landingPages", label: "Landing page content and offers" },
          {
            value: "bidStrategy",
            label: "Estimated bid strategy and spending",
          },
          { value: "rankings", label: "Organic vs paid rankings" },
        ],
      },
    ],
    landingPages: [
      {
        id: "landingPageFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which landing page optimization features would be most valuable? (Select up to 2)",
        options: [
          {
            value: "speedMonitor",
            label: "Page-speed & mobile-friendliness monitor",
          },
          { value: "conversionViz", label: "Conversion-rate trend visualizer" },
          {
            value: "pauseRule",
            label: "Rule: pause ads linking to slow pages",
          },
          {
            value: "llmAudit",
            label: "Screenshot + LLM audit of page content",
          },
          { value: "abTesting", label: "A/B test management UI" },
        ],
        maxSelections: 2,
      },
      {
        id: "landingPageMetrics",
        type: "checkbox",
        required: false,
        question:
          "Which landing page metrics matter most to you? (Select up to 3)",
        options: [
          { value: "loadSpeed", label: "Page load speed" },
          { value: "bounceRate", label: "Bounce rate" },
          { value: "conversionRate", label: "Conversion rate" },
          { value: "timeOnPage", label: "Time on page" },
          { value: "mobileFriendliness", label: "Mobile-friendliness score" },
          { value: "coreWebVitals", label: "Core Web Vitals metrics" },
        ],
        maxSelections: 3,
      },
    ],
    assets: [
      {
        id: "assetFeatures",
        type: "checkbox",
        required: false,
        question:
          "Which asset/extension management features would be most valuable? (Select up to 2)",
        options: [
          {
            value: "audit",
            label: "Structured snippets / callouts / sitelinks audit",
          },
          {
            value: "autoRules",
            label: "Automated enable/disable rules by performance",
          },
          {
            value: "llmSuggestions",
            label: "LLM-suggested new extension copy",
          },
          { value: "bulkImport", label: "Bulk import via CSV or API" },
          { value: "roiViz", label: "Visualization of asset ROI" },
        ],
        maxSelections: 2,
      },
      {
        id: "assetTypes",
        type: "checkbox",
        required: false,
        question:
          "Which ad extensions do you use most frequently? (Select all that apply)",
        options: [
          { value: "sitelinks", label: "Sitelink extensions" },
          { value: "callouts", label: "Callout extensions" },
          { value: "structured", label: "Structured snippet extensions" },
          { value: "price", label: "Price extensions" },
          { value: "promotion", label: "Promotion extensions" },
          { value: "lead", label: "Lead form extensions" },
          { value: "location", label: "Location extensions" },
        ],
      },
    ],
  };

  // Universal follow-up questions for all paths
  const universalFollowUpQuestions = [
    {
      id: "reportingPreference",
      type: "radio",
      required: false,
      question:
        "How would you prefer to receive insights and reports from the tool?",
      options: [
        { value: "email", label: "Email summaries (daily/weekly/monthly)" },
        { value: "dashboard", label: "Dashboard within the tool" },
        {
          value: "lookerStudio",
          label: "Google Data Studio / Looker Studio integration",
        },
        { value: "api", label: "API access for custom reporting" },
        {
          value: "notifications",
          label: "Slack/Teams notifications for alerts",
        },
      ],
    },
    {
      id: "integrationPriority",
      type: "checkbox",
      required: false,
      question:
        "Which external systems would you want this tool to integrate with? (Select all that apply)",
      options: [
        { value: "analytics", label: "Google Analytics" },
        { value: "crm", label: "CRM systems (Salesforce, HubSpot, etc.)" },
        { value: "sheets", label: "Google Sheets" },
        { value: "bi", label: "Business Intelligence tools" },
        { value: "webAnalytics", label: "Web analytics platforms" },
        { value: "none", label: "None/not important" },
      ],
    },
    {
      id: "pricingModel",
      type: "radio",
      required: false,
      question:
        "Which pricing model would you prefer for this automation tool?",
      options: [
        { value: "flatMonthly", label: "Flat monthly subscription" },
        {
          value: "tieredSpend",
          label: "Tiered pricing based on ad spend managed",
        },
        {
          value: "tieredFeatures",
          label: "Tiered pricing based on features used",
        },
        { value: "payPerUse", label: "Pay-per-use model" },
        { value: "freemium", label: "Freemium with paid advanced features" },
      ],
    },
  ];

  // Initialize the full question path when primary features are selected
  useEffect(() => {
    if (
      responses.primaryFeature &&
      Array.isArray(responses.primaryFeature) &&
      responses.primaryFeature.length > 0
    ) {
      // Get the first selected feature for the initial path
      // In a full implementation, you could build a more complex path based on all selections
      const selectedFeature = responses.primaryFeature[0];
      const featurePath = branchingPaths[selectedFeature] || [];
      setQuestionPath([...featurePath, ...universalFollowUpQuestions]);
    }
  }, [responses.primaryFeature]);

  // Check if all required questions are complete
  useEffect(() => {
    const requiredQuestionIds = initialQuestions
      .filter((q) => q.required)
      .map((q) => q.id);

    const answeredRequiredQuestions = requiredQuestionIds.filter(
      (id) =>
        responses[id] !== undefined &&
        (typeof responses[id] === "string" ||
          (Array.isArray(responses[id]) && responses[id].length > 0)),
    );

    setRequiredQuestionsComplete(
      answeredRequiredQuestions.length === requiredQuestionIds.length,
    );

    // Calculate progress percentage
    if (currentQuestionIndex < initialQuestions.length) {
      // Within required questions
      setProgressPercentage(
        Math.round((currentQuestionIndex / initialQuestions.length) * 50),
      );
    } else if (questionPath.length > 0) {
      // Within branching questions
      const branchProgress = currentQuestionIndex - initialQuestions.length;
      const additionalPercentage = Math.round(
        (branchProgress / questionPath.length) * 50,
      );
      setProgressPercentage(50 + additionalPercentage);
    }
  }, [responses, currentQuestionIndex, initialQuestions, questionPath]);

  // All questions (required + branching path)
  const allQuestions = [...initialQuestions, ...questionPath];

  // Handle response changes
  const handleResponseChange = (questionId, value, type) => {
    if (type === "checkbox") {
      // For checkboxes, toggle the selection
      const currentValues: string[] = Array.isArray(responses[questionId])
        ? (responses[questionId] as string[])
        : [];
      let newValues;

      if (currentValues.includes(value)) {
        // Remove if already selected
        newValues = currentValues.filter((v) => v !== value);
      } else {
        // Add if not selected
        const question = allQuestions.find((q) => q.id === questionId);
        const maxSelections = question?.maxSelections;

        if (maxSelections && currentValues.length >= maxSelections) {
          // If max selections reached, remove the first item and add the new one
          newValues = [...currentValues.slice(1), value];
        } else {
          // Otherwise just add the new value
          newValues = [...currentValues, value];
        }
      }

      setResponses({
        ...responses,
        [questionId]: newValues,
      });
    } else {
      // For radio buttons, just set the value
      setResponses({
        ...responses,
        [questionId]: value,
      });
    }
  };

  // Handle next question
  const handleNext = () => {
    // Check if current question is answered
    const currentQuestion = allQuestions[currentQuestionIndex];
    const response = responses[currentQuestion.id];

    if (
      currentQuestion.required &&
      (response === undefined ||
        (Array.isArray(response) && response.length === 0))
    ) {
      alert("Please answer this question before continuing.");
      return;
    }

    // If this is the last question, complete the survey
    if (currentQuestionIndex >= allQuestions.length - 1) {
      setSurveyComplete(true);
      return;
    }

    // Move to the next question
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  // Handle previous question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    setSurveyComplete(true);
    // Send the responses to Supabase
    try {
      console.log("Submitting survey responses:", responses);

      // Ensure the responses object has data
      if (Object.keys(responses).length === 0) {
        console.error("No responses to submit!");
        alert("Error: No survey responses to submit. Please try again.");
        return;
      }

      console.log("Attempting to save responses to simple_surveys table...");
      // Using the new table with simpler structure
      const { error, data } = await supabase
        .from("simple_surveys")
        .insert({
          data: responses, // Store responses in the data column
        })
        .select();

      if (error) {
        console.error("Error saving survey responses:", error);
        alert("Error saving survey: " + error.message);
        return;
      }

      console.log("Survey responses saved to Supabase successfully!");
    } catch (err) {
      console.error("Error saving survey responses:", err);
      alert(
        "Error saving survey: " +
          (err instanceof Error ? err.message : String(err)),
      );
    }
  };

  // If survey is complete, show the thank you screen
  if (surveyComplete) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-center mb-8">
          Thanks for completing the survey!
        </h2>
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="text-xl font-semibold mb-3">
            Your Google Ads Automation Preferences
          </h3>
          <p className="mb-3">
            Based on your responses, here are the top features that would
            benefit your workflow:
          </p>

          {responses.primaryFeature &&
            Array.isArray(responses.primaryFeature) &&
            responses.primaryFeature.length > 0 && (
              <div className="mb-4">
                <p className="font-medium">Top Feature Areas:</p>
                <ul className="ml-4 list-disc pl-4">
                  {(responses.primaryFeature as string[]).map((feature) => (
                    <li key={feature}>
                      {
                        allQuestions
                          .find((q) => q.id === "primaryFeature")
                          ?.options.find((o) => o.value === feature)?.label
                      }
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {responses.automationPreference && (
            <div className="mb-4">
              <p className="font-medium">Automation Approach:</p>
              <p className="ml-4">
                {
                  allQuestions
                    .find((q) => q.id === "automationPreference")
                    ?.options.find(
                      (o) => o.value === responses.automationPreference,
                    )?.label
                }
              </p>
            </div>
          )}

          {responses.painPoints && responses.painPoints.length > 0 && (
            <div className="mb-4">
              <p className="font-medium">Key Pain Points:</p>
              <ul className="ml-4 list-disc pl-4">
                {(responses.painPoints as string[]).map((point) => (
                  <li key={point}>
                    {
                      allQuestions
                        .find((q) => q.id === "painPoints")
                        ?.options.find((o) => o.value === point)?.label
                    }
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <button
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          onClick={() => window.location.reload()}
        >
          Start a New Survey
        </button>

        <p className="text-center mt-6 text-gray-600">
          Your responses have been recorded. Thank you for helping shape the
          future of Google Ads automation!
        </p>
      </div>
    );
  }

  // Current question
  const currentQuestion = allQuestions[currentQuestionIndex];

  // Render the current question
  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <h1 className="text-2xl font-bold text-center mb-2">
        Google Ads Automation Survey
      </h1>
      <p className="text-center text-gray-600 mb-6">
        Help us build the features you need most
      </p>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
        <div
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>

      {/* Progress text */}
      <div className="flex justify-between text-sm text-gray-600 mb-8">
        <span>
          {currentQuestionIndex < initialQuestions.length ? (
            <>
              Required Question {currentQuestionIndex + 1} of{" "}
              {initialQuestions.length}
            </>
          ) : (
            <>
              Optional Question{" "}
              {currentQuestionIndex - initialQuestions.length + 1} of{" "}
              {questionPath.length}
            </>
          )}
        </span>
        {requiredQuestionsComplete && (
          <span>Required questions complete ✓</span>
        )}
      </div>

      {/* Question */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          {currentQuestion.question}
        </h2>

        {/* Options */}
        <div className="space-y-3">
          {currentQuestion.options.map((option) => {
            // NullGuard: ensure we only call includes on array
            const currentValues: string[] = Array.isArray(
              responses[currentQuestion.id],
            )
              ? (responses[currentQuestion.id] as string[])
              : [];
            const isChecked =
              currentQuestion.type === "checkbox"
                ? currentValues.includes(option.value)
                : responses[currentQuestion.id] === option.value;

            return (
              <div key={option.value} className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id={`${currentQuestion.id}-${option.value}`}
                    name={currentQuestion.id}
                    type={currentQuestion.type}
                    checked={isChecked}
                    onChange={() =>
                      handleResponseChange(
                        currentQuestion.id,
                        option.value,
                        currentQuestion.type,
                      )
                    }
                    className={
                      currentQuestion.type === "checkbox"
                        ? "h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        : "h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    }
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor={`${currentQuestion.id}-${option.value}`}
                    className="font-medium text-gray-700 cursor-pointer"
                  >
                    {option.label}
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        {/* Hint text for max selections */}
        {currentQuestion.type === "checkbox" &&
          currentQuestion.maxSelections && (
            <p className="text-sm text-gray-500 mt-2">
              Select up to {currentQuestion.maxSelections}{" "}
              {currentQuestion.maxSelections === 1 ? "option" : "options"}
            </p>
          )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className={`py-2 px-4 rounded-lg ${
            currentQuestionIndex === 0
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Previous
        </button>

        <div>
          {currentQuestionIndex >= initialQuestions.length - 1 &&
            requiredQuestionsComplete && (
              <button
                onClick={handleSubmit}
                className="py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 mr-2"
              >
                Submit Survey
              </button>
            )}

          <button
            onClick={handleNext}
            className="py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {currentQuestionIndex >= allQuestions.length - 1
              ? "Finish"
              : "Next"}
          </button>
        </div>
      </div>

      {/* Help text */}
      {currentQuestionIndex >= initialQuestions.length && (
        <p className="text-sm text-gray-500 mt-6 text-center">
          You've completed all required questions. These additional questions
          are optional, but continuing will help us better understand your
          needs.
        </p>
      )}

      {/* Optional section header */}
      {currentQuestionIndex === initialQuestions.length && (
        <div className="bg-blue-50 p-3 rounded-lg my-4 text-center">
          <p className="font-medium text-blue-800">
            Optional Questions Section
          </p>
          <p className="text-sm text-blue-600">
            Based on your selection of "
            {Array.isArray(responses.primaryFeature)
              ? allQuestions
                  .find((q) => q.id === "primaryFeature")
                  ?.options.find(
                    (o) =>
                      o.value === (responses.primaryFeature as string[])[0],
                  )?.label
              : ""}
            ", we have a few follow-up questions.
          </p>
        </div>
      )}
    </div>
  );
};

export default GoogleAdsSurvey;
